import React from 'react';
import { Zap, Shield, AlertCircle, Camera } from 'lucide-react';
import image1 from "../images/AIPowered.png";
import image2 from "../images/Guidance.png";
import image3 from "../images/PeaceofMind.png";
import image4 from "../images/MemoryCapture.png";


export default function Features() {
  const features = [
    {
      title: 'AI-Powered Planning',
      description: 'Not just a route, your route. Sarayu tailors it to your mood, finding pet-friendly paths with great coffee stops, scenic views, or the fastest way there.',
      benefits: [
        'Intelligent route optimization based on preferences',
        'Mood-based journey customization',
        'Real-time availability updates',
        'Smart budget allocation',
      ],
      icon: <Zap className="w-8 h-10 text-indigo-600" />,
      image: image1,
    },
    {
      title: 'Proactive Guidance',
      description: 'Sarayu\'s AI constantly analyzes traffic, weather, and potential hazards, rerouting you before you ever hit trouble, ensuring a smooth journey.',
      benefits: [
        'Real-time hazard detection',
        'Weather and traffic predictions',
        'Automatic rerouting before problems arise',
        'Emergency preparedness alerts',
      ],
      icon: <Shield className="w-8 h-10 text-emerald-600" />,
      image: image2,
    },
    {
      title: 'One-Tap Peace of Mind',
      description: 'In a crisis, one tap instantly connects you to the right help, sharing your precise location and vehicle information for immediate assistance.',
      benefits: [
        'Instant emergency response connection',
        'Automatic location sharing',
        'Vehicle information transmission',
        'Emergency contacts at your fingertips',
      ],
      icon: <AlertCircle className="w-8 h-10 text-blue-600" />,
      image: image3,
    },
    {
      title: 'Effortless Memory Capture',
      description: 'Enjoy every moment of the view. Sarayu automatically creates a beautiful digital scrapbook of your trip, so you don\'t miss capturing any memories.',
      benefits: [
        'AI photo curation and selection',
        'Automatic journey log with stops and weather',
        'Generative AI storytelling with maps',
        'Beautiful digital scrapbooks',
      ],
      icon: <Camera className="w-8 h-10 text-pink-600" />,
      image: image4,
    },
  ];


  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 pt-32 bg-gradient-to-br from-indigo-50 via-background to-emerald-50 heroimg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 animate-slideUp">
            Intelligence Made Simple
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-slideUp" style={{ animationDelay: '0.1s' }}>
            Every feature is designed to make your journey smarter, safer, and more memorable.
          </p>
        </div>
      </section>


      {/* Features Sections */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`mb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                index % 2 === 1 ? 'lg:grid-cols-2 lg:auto-cols-max' : ''
              }`}
            >
              {/* Content */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <h2 className="flex flex-row text-4xl gap-2 font-bold text-gray-900 mb-4">{feature.icon}{feature.title}</h2>
                <p className="text-lg text-gray-600 mb-8">{feature.description}</p>


                {/* Benefits List */}
                <div className="space-y-4">
                  {feature.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="flex items-center justify-center h-6 w-6 rounded-full bg-indigo-100">
                          <svg
                            className="h-4 w-4 text-indigo-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      </div>
                      <p className="text-gray-700 font-medium">{benefit}</p>
                    </div>
                  ))}
                </div>
              </div>


              {/* Visual */}
             <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
              <div className="glass rounded-3xl h-96 flex items-center justify-center opacity-80 overflow-hidden group">
                <img 
                  src={feature.image} 
                  alt={feature.title} 
                  className="w-full h-full object-cover rounded-3xl transition-all duration-500 ease-in-out group-hover:scale-110 group-hover:shadow-4xl" 
                />
              </div>
            </div>
            </div>
          ))}
        </div>
      </section>


      {/* CTA Section */}
      {/* <section className="py-20 bg-gradient-to-r from-indigo-600 to-emerald-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            Experience the Future of Travel
          </h2>
          <p className="text-xl text-black/90 mb-8">
            Join the revolution and discover how AI can transform your journeys.
          </p>
          <a
            href="/contact"
            className="inline-block px-8 py-4 bg-white text-indigo-600 font-bold rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
          >
            Get Started Today
          </a>
        </div>
      </section> */}
    </div>
  );
}
